import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Calendar, Users, ArrowLeft, CheckCircle, Phone, Mail } from 'lucide-react';
import { getEvent, registerForEvent, getImageUrl, formatDateET } from '../utils/api';
import { toast } from 'sonner';
import './EventPage.css';

const EventPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subscribe_newsletter: true
  });

  useEffect(() => {
    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      // eventId can be either slug or id
      const data = await getEvent(eventId);
      setEvent(data);
    } catch (error) {
      console.error('Failed to load event', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return formatDateET(dateStr, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegistering(true);

    try {
      // Use slug or id for registration
      await registerForEvent(event.slug || event.id, formData);
      setRegistered(true);
      toast.success('Successfully registered for the event!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to register. Please try again.';
      toast.error(message);
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="event-page">
        <div className="loading-container">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-page">
        <div className="error-container">
          <h1>Event Not Found</h1>
          <p>The event you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="event-page">
      {/* Event Hero */}
      <section className="event-hero">
        <div className="event-hero-bg" style={{ backgroundImage: `url(${getImageUrl(event.hero_image || event.image)})` }}></div>
        <div className="event-hero-overlay"></div>
        
        <div className="container event-hero-content">
          <Link to="/events" className="back-link">
            <ArrowLeft size={20} />
            Back to Events
          </Link>
          
          <h1 data-testid="event-title">{event.title}</h1>
          
          <div className="event-meta-badges">
            <div className="meta-badge">
              <Calendar size={18} />
              <span>{formatDate(event.date)}</span>
            </div>
            <div className="meta-badge">
              <Clock size={18} />
              <span>{event.time}</span>
            </div>
            <div className="meta-badge">
              <MapPin size={18} />
              <span>{event.venue}, {event.city}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Event Details */}
      <section className="event-details-section">
        <div className="container">
          <div className="event-content-grid">
            {/* Event Image */}
            {event.image && (
              <div className="event-image-column">
                <img src={getImageUrl(event.image)} alt={event.title} className="event-detail-image" />
              </div>
            )}
            
            {/* Left Column - Event Info */}
            <div className="event-info-column">
              {event.description && (
                <div className="event-description">
                  <h2>About This Event</h2>
                  <div className="event-description-text">
                    {event.description.split('\n').map((paragraph, index) => (
                      paragraph.trim() && (
                        <p key={index} style={{ marginBottom: '1rem', whiteSpace: 'normal' }}>
                          {paragraph}
                        </p>
                      )
                    ))}
                  </div>
                </div>
              )}

              {event.lineup && event.lineup.length > 0 && (
                <div className="event-lineup">
                  <h2>Lineup</h2>
                  <div className="lineup-list">
                    {event.lineup.map((artist, index) => (
                      <div key={index} className="lineup-artist">
                        <Users size={18} />
                        <span>{artist}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="event-location-details">
                <h2>Location</h2>
                <p className="venue-name">{event.venue}</p>
                <p className="venue-address">{event.location || `${event.city}`}</p>
              </div>
            </div>

            {/* Right Column - Registration Form */}
            <div className="event-registration-column">
              <div className="registration-card">
                {!event.registration_enabled ? (
                  <div className="registration-closed">
                    <h3>Registration Closed</h3>
                    <p>Registration for this event is currently not available.</p>
                  </div>
                ) : registered ? (
                  <div className="registration-success" data-testid="registration-success">
                    <CheckCircle size={48} />
                    <h3>You're Registered!</h3>
                    <p>Thank you for registering. We've sent a confirmation to your email.</p>
                    <p className="registered-email">{formData.email}</p>
                  </div>
                ) : (
                  <>
                    <h3>Register for This Event</h3>
                    <p className="registration-subtitle">Secure your spot at this exclusive event</p>
                    
                    <form onSubmit={handleSubmit} className="registration-form" data-testid="registration-form">
                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Enter your full name"
                          required
                          data-testid="registration-name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Enter your email"
                          required
                          data-testid="registration-email"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1 (555) 123-4567"
                          data-testid="registration-phone"
                        />
                      </div>

                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.subscribe_newsletter}
                            onChange={(e) => setFormData({ ...formData, subscribe_newsletter: e.target.checked })}
                            data-testid="registration-newsletter"
                          />
                          <span className="checkbox-text">Contact me about future events and AMR news</span>
                        </label>
                      </div>

                      <button 
                        type="submit" 
                        className="btn btn-primary register-btn"
                        disabled={registering}
                        data-testid="registration-submit"
                      >
                        {registering ? 'Registering...' : 'Register Now'}
                      </button>

                      <p className="registration-note">
                        By registering, you agree to receive event updates via email.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventPage;
